import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useReducedMotion, useSpring } from 'framer-motion';
import './styles.css';
import './auth/auth.css';
import { EASE_OUT, TextReveal, useMediaQuery } from './motion';
import { SmoothScroll } from './smooth-scroll';
import { FlowingFabric } from './auth/FlowingFabric';
import { AIAnnotations } from './auth/AIAnnotations';
/* ============================================================================
 * Sign-in shell: a white canvas with a sculpted, slowly moving sheet of cloth
 * carrying the product's signals, and the form set on the right (desktop) or
 * under a compact cloth header (tablet / mobile). Purely presentational — the
 * form and all auth logic are passed in as children.
 * ========================================================================== */
// MUI TextField styling for the sign-in inputs.
export const landingFieldSx = {
    '& .MuiOutlinedInput-root': {
        height: '52px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        fontSize: '15px',
        fontFamily: 'var(--f-sans)',
        color: '#0B1020',
        transition: 'box-shadow 0.45s cubic-bezier(0.16, 1, 0.3, 1), background-color 0.3s',
    },
    '& .MuiOutlinedInput-input::placeholder': { color: '#8A93A6', opacity: 1 },
    '& .MuiOutlinedInput-input:-webkit-autofill': {
        WebkitBoxShadow: '0 0 0 100px #fff inset',
        WebkitTextFillColor: '#0B1020',
    },
    '& fieldset': {
        borderColor: 'rgba(11, 16, 32, 0.13)',
        transition: 'border-color 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
    },
    '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'rgba(11, 16, 32, 0.28)' },
    '& .MuiOutlinedInput-root.Mui-focused': {
        boxShadow: '0 0 0 4px rgba(49, 91, 255, 0.09), 0 8px 24px -12px rgba(49, 91, 255, 0.35)',
    },
    '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#315BFF', borderWidth: '1px' },
    '& .MuiOutlinedInput-root.Mui-error fieldset': { borderColor: '#D92D20' },
    '& .MuiFormHelperText-root': { marginLeft: 0, fontFamily: 'var(--f-sans)', fontSize: '12.5px' },
    '& .MuiInputAdornment-root .MuiIconButton-root': {
        color: '#8A93A6',
        transition: 'color 0.25s',
        '&:hover': { color: '#315BFF', backgroundColor: 'rgba(49, 91, 255, 0.06)' },
        '&.Mui-focusVisible': { outline: '2px solid #315BFF', outlineOffset: '1px' },
    },
};
const SUBMIT_EASE = [0.16, 1, 0.3, 1];
const submitFill = {
    rest: { scaleY: 0, transition: { duration: 0.35, ease: SUBMIT_EASE } },
    hover: { scaleY: 1, transition: { duration: 0.45, ease: SUBMIT_EASE } },
};
// The track holds two stacked copies of the label, so -50% is one line.
const submitRoll = {
    rest: { y: '0%', transition: { duration: 0.4, ease: SUBMIT_EASE } },
    hover: { y: '-50%', transition: { duration: 0.45, ease: SUBMIT_EASE } },
};
const arrowOut = {
    rest: { x: 0, opacity: 1, transition: { duration: 0.35, ease: SUBMIT_EASE } },
    hover: { x: 16, opacity: 0, transition: { duration: 0.3, ease: SUBMIT_EASE } },
};
const arrowIn = {
    rest: { x: -16, opacity: 0, transition: { duration: 0.25, ease: SUBMIT_EASE } },
    hover: { x: 0, opacity: 1, transition: { duration: 0.4, delay: 0.08, ease: SUBMIT_EASE } },
};
function SubmitArrow() {
    return (_jsx("svg", { width: '16', height: '16', viewBox: '0 0 16 16', fill: 'none', children: _jsx("path", { d: 'M3 8h10M9 4l4 4-4 4', stroke: 'currentColor', strokeWidth: '1.8', strokeLinecap: 'round', strokeLinejoin: 'round' }) }));
}
/**
 * Primary submit for the auth forms — the same motion as the landing CTAs:
 * a slight magnetic pull, the label rolls, the arrow hands off, a darker fill
 * sweeps up, and it settles on press. While pending it holds still and shows
 * the progress bar.
 */
export function AuthSubmit({ pending, label, pendingLabel, }) {
    const reduced = useReducedMotion();
    const x = useSpring(0, { stiffness: 220, damping: 20, mass: 0.5 });
    const y = useSpring(0, { stiffness: 220, damping: 20, mass: 0.5 });
    const onMove = (e) => {
        if (pending || reduced || e.pointerType !== 'mouse')
            return;
        const r = e.currentTarget.getBoundingClientRect();
        x.set((e.clientX - (r.left + r.width / 2)) * 0.04);
        y.set((e.clientY - (r.top + r.height / 2)) * 0.18);
    };
    const reset = () => {
        x.set(0);
        y.set(0);
    };
    const text = pending ? pendingLabel : label;
    return (_jsxs(motion.button, { type: 'submit', disabled: pending, "aria-busy": pending, className: 'vx-submit', style: { x, y }, onPointerMove: onMove, onPointerLeave: reset, initial: 'rest', animate: 'rest', whileHover: pending ? undefined : 'hover', whileFocus: pending ? undefined : 'hover', whileTap: pending ? undefined : { scale: 0.985 }, children: [_jsx(motion.span, { className: 'vx-submit__fill', variants: submitFill, "aria-hidden": 'true' }), _jsx("span", { className: 'vx-submit__label', children: _jsxs(motion.span, { className: 'vx-submit__track', variants: pending ? undefined : submitRoll, children: [_jsx("span", { children: text }), _jsx("span", { "aria-hidden": 'true', children: text })] }) }), pending ? (_jsx("span", { className: 'vx-submit__progress', "aria-hidden": 'true' })) : (_jsxs("span", { className: 'vx-submit__arrows', "aria-hidden": 'true', children: [_jsx(motion.span, { variants: arrowOut, children: _jsx(SubmitArrow, {}) }), _jsx(motion.span, { variants: arrowIn, children: _jsx(SubmitArrow, {}) })] }))] }));
}
function ProductHeader() {
    return (_jsxs("header", { className: 'vx-auth__top', children: [_jsxs("a", { href: 'https://vriti.ai', className: 'vx-auth__brand', "aria-label": 'Vriti home', children: [_jsx("img", { src: '/logo.png', alt: '', width: 30, height: 30 }), _jsxs("span", { className: 'vx-auth__pill', children: [_jsx("i", { "aria-hidden": 'true' }), "AI Interview"] })] }), _jsxs(Link, { to: '/', className: 'vx-auth__back', children: [_jsx("span", { "aria-hidden": 'true', children: "\u2190" }), " Product overview"] })] }));
}
/** Pointer across the viewport, -1 → 1 per axis, spring-smoothed. */
function usePointer(enabled) {
    const rawX = useMotionValue(0);
    const rawY = useMotionValue(0);
    const active = useMotionValue(0);
    const x = useSpring(rawX, { stiffness: 38, damping: 16, mass: 1 });
    const y = useSpring(rawY, { stiffness: 38, damping: 16, mass: 1 });
    useEffect(() => {
        if (!enabled || !window.matchMedia('(pointer: fine)').matches)
            return undefined;
        const move = (e) => {
            rawX.set((e.clientX / window.innerWidth) * 2 - 1);
            rawY.set((e.clientY / window.innerHeight) * 2 - 1);
            active.set(1);
        };
        window.addEventListener('pointermove', move, { passive: true });
        return () => window.removeEventListener('pointermove', move);
    }, [enabled, rawX, rawY, active]);
    const [pointer] = useState(() => ({ x, y, active }));
    return pointer;
}
/** Width the desktop form column reserves — mirrors --vx-col in auth.css. */
const columnWidth = (wide) => wide
    ? Math.min(760, Math.max(560, window.innerWidth * 0.47))
    : Math.min(560, Math.max(420, window.innerWidth * 0.36));
function useColumnWidth(on, wide) {
    const [w, setW] = useState(() => columnWidth(wide));
    useEffect(() => {
        if (!on)
            return undefined;
        const onResize = () => setW(columnWidth(wide));
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [on, wide]);
    return on ? w : 0;
}
export function AuthShell({ kicker = 'Sign in', title, footer, wide = false, nativeScroll = false, children, }) {
    const reduced = !!useReducedMotion();
    const desktop = useMediaQuery('(min-width: 1024px)');
    const tablet = useMediaQuery('(min-width: 640px)');
    const pointer = usePointer(!reduced);
    const reserve = useColumnWidth(desktop, wide);
    const variant = desktop ? 'full' : 'compact';
    const rise = (delay, y = 18) => reduced
        ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.3 } }
        : {
            initial: { opacity: 0, y },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 1.2, delay, ease: EASE_OUT },
        };
    const [first, ...rest] = title.split(' ');
    return (
    // Lenis smooth scrolling, as on the landing (fine pointers only; touch keeps native).
    _jsx(SmoothScroll, { children: _jsxs("div", { className: `vx vx-auth${wide ? ' vx-auth--wide' : ''}`, "data-lenis-prevent": nativeScroll || undefined, children: [_jsx(ProductHeader, {}), _jsx("div", { className: 'vx-auth__stage', "aria-hidden": 'true', children: _jsx(motion.div, { className: 'vx-auth__cloth', initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: reduced ? 0.3 : 1.8, delay: reduced ? 0 : 0.3, ease: EASE_OUT }, children: _jsx(FlowingFabric, { variant: variant, reserveRight: reserve, pointer: pointer, reduced: reduced, children: tablet && _jsx(AIAnnotations, { variant: variant, start: reduced ? 0 : 1.5 }) }, variant) }) }), _jsxs("section", { className: 'vx-auth__hero', children: [_jsxs(motion.p, Object.assign({ className: 'vx-auth__eyebrow' }, rise(0.7, 10), { children: [_jsx("i", { "aria-hidden": 'true' }), "Vriti AI Interview \u00B7 Recruiter workspace"] })), _jsxs("p", { className: 'vx-auth__headline', children: [_jsx(TextReveal, { text: 'AI screening interviews,', by: 'word', play: true, delay: reduced ? 0 : 0.9, stagger: 0.07, className: 'vx-auth__line' }), _jsx(motion.span, { className: 'vx-auth__line vx-auth__line--serif', initial: reduced ? { opacity: 0 } : { opacity: 0, y: '0.45em' }, animate: { opacity: 1, y: 0 }, transition: {
                                        duration: reduced ? 0.3 : 1.4,
                                        delay: reduced ? 0 : 1.15,
                                        ease: EASE_OUT,
                                    }, children: "built on evidence." })] })] }), _jsxs("main", { className: 'vx-auth__main', children: [_jsxs("div", { className: 'vx-auth__panel', children: [_jsxs(motion.p, Object.assign({ className: 'vx-auth__kicker' }, rise(1.1, 10), { children: [_jsx("span", { className: 'vx-dot vx-dot--live', "aria-hidden": 'true' }), kicker] })), _jsxs(motion.h1, Object.assign({ className: 'vx-auth__h1' }, rise(1.15), { children: [first, " ", rest.length > 0 && _jsx("em", { children: rest.join(' ') })] })), _jsx(motion.div, Object.assign({}, rise(1.3, 14), { children: children }))] }), _jsx(motion.p, Object.assign({ className: 'vx-auth__foot' }, rise(1.4, 0), { children: _jsxs("span", { children: ["\u00A9 ", new Date().getFullYear(), " ", footer] }) }))] })] }) }));
}
